import React, { FC, useContext } from "react"
import { CourseTableType } from "./CourseTableColumns"
import css from "./BookingButton.module.css"
import { Link } from "react-router-dom"
import { Button, message, Tooltip } from "antd"
import { CoursesContext } from "./CourseTableContainer"
import { URL_COURSE_DETAILS } from "../../models/url"
import { blue } from "@ant-design/colors"
import { useMediaSize } from "../../layouts/Main/GetWindowDimensions/GetWindowDimensions"
import { InfoCircleOutlined } from "@ant-design/icons"
import { useCourseFilterContext } from "./CourseFilterContext"
import { persistLastViewedCourse } from "../../helpers/lastViewedCourse"
import { parentScrollToOffset } from "../../helpers/parentIFrame"
import { CourseBookingBasePriceBlockingReason } from "../../generated/graphql"
import i18n from "../../i18n"

type BookingButtonProps = { record: CourseTableType; block?: boolean; style?: React.CSSProperties }

const BookingButton: FC<BookingButtonProps> = ({ record, block, style }) => {
    const mediaSize = useMediaSize()
    const { media } = mediaSize
    const { handleOpen } = useContext(CoursesContext)
    // Defensiv: kann undefined sein (z.B. Tests ohne Provider) -> ctx?.-Zugriff.
    const ctx = useCourseFilterContext()

    const handleClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        // Zuletzt angesehenen Kurs merken, damit die Liste beim Zurück dorthin
        // scrollt und ihn kurz hervorhebt. Nur im Link-Pfad relevant (nicht bei
        // "ausgebucht"/disabled, dort gibt es keinen Klick in die Details).
        if (ctx?.lastViewedCourseKey) {
            persistLastViewedCourse(ctx.lastViewedCourseKey, record.id)
        }
        // Eltern-Viewport an den iFrame-Anfang scrollen (No-Op ohne parentIFrame).
        parentScrollToOffset(0, 0)
        if (handleOpen) handleOpen(record)
    }

    const courseDetailsLink = URL_COURSE_DETAILS.replace(":id", record.id)

    type CoursePlaceStatus = "freePlaces" | "waitlist" | "full" | "blocked" | "notBookable"

    // Späteinstieg-Sperre: Meldet die Server-Quote den Kurs als nicht buchbar
    // (z. B. bereits abgeschlossen oder zu wenige verbleibende Einheiten), wird
    // fail-closed sowohl Buchen als auch Warteliste gesperrt. Fehlt die Quote,
    // bleibt das Verhalten unverändert (additive Degradation).
    const lateEntryBlockingReason = () => {
        const quote = record.bookingPriceQuote
        if (!quote || quote.isBookable !== false) {
            return null
        }
        if (quote.blockingReason === CourseBookingBasePriceBlockingReason.NoEligibleLessons) {
            return i18n.t("lateEntry.notBookableCourseFinished", {
                defaultValue: "Dieser Kurs ist bereits abgeschlossen und kann nicht mehr gebucht werden.",
            })
        }
        if (quote.blockingReason === CourseBookingBasePriceBlockingReason.MinimumPartialBookingLessonCountNotReached) {
            return i18n.t("lateEntry.notBookableTooFewLessons", {
                defaultValue: "Für diesen Kurs sind nicht mehr genügend Kurseinheiten verfügbar.",
            })
        }
        // isBookable === false ohne bekannten Grund: dennoch fail-closed sperren.
        return i18n.t("lateEntry.notBookableButton", { defaultValue: "Nicht buchbar" })
    }

    const coursePlaceStatus = (): CoursePlaceStatus => {
        const maxWaitlist = record.maxWaitlist ?? undefined
        const waitListHasMax = maxWaitlist !== null && maxWaitlist !== undefined
        const remainingWaitlistPlaces = maxWaitlist ? maxWaitlist - record.waitListCount : 0
        const bookingIsBlocked = record.blockBookingOnline !== false

        if (bookingIsBlocked) {
            return "blocked"
        }
        if (record.bookingPriceQuote && record.bookingPriceQuote.isBookable === false) {
            return "notBookable"
        }
        if (record.freePlaces > 0) {
            return "freePlaces"
        } else if (waitListHasMax && remainingWaitlistPlaces <= 0) {
            return "full"
        }

        return "waitlist"
    }

    const buttonText = () => {
        switch (coursePlaceStatus()) {
            case "blocked":
                return "Nicht buchbar"
            case "notBookable":
                return i18n.t("lateEntry.notBookableButton", { defaultValue: "Nicht buchbar" })
            case "freePlaces":
                return media === "sm" ? "Buchen" : "Details & Buchen"
            case "full":
                return "Ausgebucht"
            case "waitlist":
                return "Warteliste"
            default:
                return "Warteliste"
        }
    }

    const tooltipText = () => {
        switch (coursePlaceStatus()) {
            case "blocked":
                return "Kurs ist nicht buchbar, klicke um mehr über den Kurs zu erfahren"
            case "notBookable":
                return lateEntryBlockingReason() ?? "Dieser Kurs ist nicht mehr buchbar."
            case "full":
                return "Kurs ist ausgebucht"
            default:
                return "Klicke um mehr über den Kurs zu erfahren"
        }
    }

    const infoText = () => {
        if (media === "xs") {
            return "Weitere Infos zum Kurs"
        }

        return <InfoCircleOutlined style={{ fontSize: "1.5em", marginTop: 4 }} />
    }

    const buttonStyle = () => {
        const waitListStyle = {
            backgroundColor: blue[3],
            borderColor: blue[3],
        }

        const fullStyle = undefined

        const freePlacesStyle = {
            backgroundColor: blue[5],
            borderColor: blue[5],
        }

        switch (coursePlaceStatus()) {
            case "freePlaces":
                return freePlacesStyle
            case "full":
                return fullStyle
            case "waitlist":
                return waitListStyle
            default:
                return waitListStyle
        }
    }

    // "full" (ausgebucht) und "notBookable" (Späteinstieg-Sperre) rendern beide einen
    // deaktivierten Button OHNE Link — so ist weder Buchen noch Warteliste erreichbar.
    const renderAsDisabledNoLink = coursePlaceStatus() === "full" || coursePlaceStatus() === "notBookable"

    return (
        <div id='bookCourseButton' className={css.bookingButton} style={style}>
            {renderAsDisabledNoLink ? (
                // Render button without link when course is full or not bookable.
                // Tooltip trägt den Grund (bei "notBookable" der verständliche Späteinstieg-Text).
                <Tooltip title={tooltipText()}>
                    {mediaSize.media === "xs" ? (
                        <Button
                            data-testid='book-course-button'
                            type='primary'
                            block={block}
                            style={{
                                ...buttonStyle(),
                                height: mediaSize.media === "xs" ? "auto" : undefined,
                                whiteSpace: mediaSize.media === "xs" ? "pre-line" : undefined,
                            }}
                            size={media === "sm" ? "small" : "middle"}
                            disabled={true}
                        >
                            {mediaSize.media === "xs" && `${infoText()} & `}
                            <span style={{ fontWeight: 600, marginLeft: 7 }}>{buttonText()}</span>
                        </Button>
                    ) : (
                        <Button
                            data-testid='book-course-button'
                            type='primary'
                            block
                            style={{
                                ...buttonStyle(),
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                height: mediaSize.media === "xs" ? "auto" : undefined,
                                whiteSpace: mediaSize.media === "xs" ? "pre-line" : undefined,
                            }}
                            size={media === "sm" ? "small" : "middle"}
                            disabled={true}
                        >
                            <span style={{ fontWeight: 500 }}>{buttonText()}</span>
                            {mediaSize.media !== "xs" && <InfoCircleOutlined style={{ fontSize: "1em", marginTop: 3 }} />}
                        </Button>
                    )}
                </Tooltip>
            ) : (
                // Render button with link when course is not full
                <Link
                    className={css.bookingButton}
                    to={{
                        // pathname: `/details/${record.id}`,
                        pathname: courseDetailsLink,
                        state: { data: record },
                    }}
                >
                    {mediaSize.media === "xs" ? (
                        <Button
                            data-testid='book-course-button'
                            type='primary'
                            block={block}
                            style={{
                                ...buttonStyle(),
                                height: mediaSize.media === "xs" ? "auto" : undefined,
                                whiteSpace: mediaSize.media === "xs" ? "pre-line" : undefined,
                            }}
                            size={media === "sm" ? "small" : "middle"}
                            onClick={(e) => handleClick(e)}
                        >
                            {mediaSize.media === "xs" && `${infoText()} & `}
                            <span style={{ fontWeight: 600, marginLeft: 7 }}>{buttonText()}</span>
                        </Button>
                    ) : (
                        <Tooltip title={tooltipText()}>
                            <Button
                                data-testid='book-course-button'
                                type='primary'
                                block
                                style={{
                                    ...buttonStyle(),
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    height: mediaSize.media === "xs" ? "auto" : undefined,
                                    whiteSpace: mediaSize.media === "xs" ? "pre-line" : undefined,
                                }}
                                size={media === "sm" ? "small" : "middle"}
                                onClick={(e) => handleClick(e)}
                            >
                                <span style={{ fontWeight: 500 }}>{buttonText()}</span>
                                {mediaSize.media !== "xs" && <InfoCircleOutlined style={{ fontSize: "1em", marginTop: 3 }} />}
                            </Button>
                        </Tooltip>
                    )}
                </Link>
            )}
        </div>
    )
}

export default BookingButton
